<template>
  <ls-table-layout>
    <div class="table-page-search-wrapper" slot="search">
      <ls-search-form
        ref="refSearchForm"
        :formList="formList"
        @search="onSearch"
        @reset="resetQuery"
      >
        <global-select
          slot="productBroadCategoryList"
          :value.sync="queryParams.productBroadCategoryList"
          selectType="selectMultipleFilter"
          requestApi="/mes/mesDiscreteJobsCc/findCategoryComboBox"
          :selectItem="{ value: 'selectedValue', label: 'label' }"
          :dropdown-match-select-width="false"
        />
        <global-select
          slot="businessTypeList"
          :value.sync="queryParams.businessTypeList"
          selectType="selectMultiple"
          requestApi="/system/lookup/data/list"
          :selectItem="{ value: 'lookupCode', label: 'lookupValue' }"
          :setParams="{ lookupType: 'MES_MO_CC_TYPE' }"
          :dropdown-match-select-width="false"
        />
        <a-range-picker
          slot="produceDate"
          v-model="produceDates"
          :allowClear="false"
          :disabled-date="disabledProduceDate"
          @calendarChange="handleProduceCalendarChange"
          @change="handleProduceDateChange"
          @openChange="handleProduceDateOpenChange"
        />
      </ls-search-form>
    </div>
    <div slot="btn">
      <a-space>
        <download-modal :apiFun="apiCuttingBedCostAnalysisExport" ref="download" />
        <a-button
          type="primary"
          @click="handleExport"
        >
          导出
        </a-button>
      </a-space>
    </div>
    <template v-slot:table>
      <div class="page-header-content">
        <ls-api-table
          ref="dataTable"
          :columns="columns"
          :tableProps="tableProps"
          :pagination="{ pageSize: 20 }"
          rowKey="recordId"
          :scroll="{ fixScreen: true, x: 5770 }"
          :toolBox="{
            density: true,
            fullScreen: false,
            configKey: $TableKey.CUTTING_BED_COST_ANALYSIS_REPORT_LIST
          }"
        >
          <template slot="dateTime" slot-scope="{ text }">
            {{ text ? $moment(text).format('YYYY-MM-DD HH:mm:ss') : '' }}
          </template>
        </ls-api-table>
      </div>
    </template>
  </ls-table-layout>
</template>

<script>
import moment from 'moment'
import {
  apiCuttingBedCostAnalysisExport,
  apiCuttingBedCostAnalysisList
} from '@/api/report/cutting-bed-cost-analysis-report'
import { generateLsTableResData } from '@/utils/util.js'
import DownloadModal from '@/views/components/download-modal/download-modal.vue'

const createDefaultProduceDates = () => [moment().startOf('day'), moment().startOf('day')]

const createDefaultQueryParams = () => {
  const today = moment().format('YYYY-MM-DD')
  return {
    productBroadCategoryList: undefined,
    businessTypeList: ['ZP'],
    employeeKeyword: undefined,
    wipEntityName: undefined,
    assemblyItemCode: undefined,
    moTicketCode: undefined,
    materialCode: undefined,
    lotNumber: undefined,
    supplierCode: undefined,
    produceStartDate: `${today} 00:00:00`,
    produceEndDate: `${today} 23:59:59`
  }
}

export default {
  name: 'CuttingBedCostAnalysisReport',
  components: { DownloadModal },
  data () {
    return {
      apiCuttingBedCostAnalysisExport,
      produceDates: createDefaultProduceDates(),
      produceDateAnchor: undefined,
      queryParams: createDefaultQueryParams(),
      tableProps: {
        requestApi: generateLsTableResData(apiCuttingBedCostAnalysisList, true),
        pageCurrent: true
      },
      formList: [
        { label: '工艺大类', decorator: ['productBroadCategoryList'], type: 'productBroadCategoryList' },
        { label: '业务类型', decorator: ['businessTypeList'], type: 'businessTypeList' },
        { label: '人员', decorator: ['employeeKeyword'], type: 'input' },
        { label: 'CC 工单号', decorator: ['wipEntityName'], type: 'input' },
        { label: '成品物料编码', decorator: ['assemblyItemCode'], type: 'input' },
        { label: 'MO 票号', decorator: ['moTicketCode'], type: 'input' },
        { label: '物料编码', decorator: ['materialCode'], type: 'input' },
        { label: '批次号', decorator: ['lotNumber'], type: 'input' },
        { label: '供应商编码', decorator: ['supplierCode'], type: 'input' },
        { label: '生产日期', decorator: ['produceDate'], type: 'produceDate' }
      ],
      columns: [
        { title: '工艺大类', dataIndex: 'productBroadCategoryName', align: 'center', width: 140, ellipsis: true },
        { title: '业务类型编码', dataIndex: 'businessType', align: 'center', width: 150, ellipsis: true },
        { title: '业务类型名称', dataIndex: 'businessTypeName', align: 'center', width: 160, ellipsis: true },
        { title: '班组编码', dataIndex: 'teamCode', align: 'center', width: 140, ellipsis: true },
        { title: '班组名称', dataIndex: 'teamName', align: 'center', width: 140, ellipsis: true },
        { title: '大工单 CC 完工时间', dataIndex: 'completionDate', align: 'center', width: 190, scopedSlots: { customRender: 'dateTime' } },
        { title: '生产日期', dataIndex: 'produceTime', align: 'center', width: 180, scopedSlots: { customRender: 'dateTime' } },
        { title: '人员编码', dataIndex: 'employeeCode', align: 'center', width: 140, ellipsis: true },
        { title: '人员名称', dataIndex: 'employeeName', align: 'center', width: 140, ellipsis: true },
        { title: 'CC 工单号', dataIndex: 'wipEntityName', align: 'center', width: 180, ellipsis: true },
        { title: '成品物料编码', dataIndex: 'assemblyItemCode', align: 'center', width: 180, ellipsis: true },
        { title: '成品物料名称', dataIndex: 'assemblyItemName', align: 'left', width: 210, ellipsis: true },
        { title: '规格', dataIndex: 'specification', align: 'left', width: 220, ellipsis: true },
        { title: 'MO 票号', dataIndex: 'moTicketCode', align: 'center', width: 180, ellipsis: true },
        { title: '物料编码', dataIndex: 'materialCode', align: 'left', width: 220, ellipsis: true },
        { title: '排产数量', dataIndex: 'planQuantity', align: 'right', width: 130 },
        { title: '真皮基准用量×排产数', dataIndex: 'standardTotalQuantity', align: 'right', width: 200 },
        { title: '真皮基准用量', dataIndex: 'standardUnitQuantity', align: 'right', width: 170 },
        { title: '单套净用量', dataIndex: 'referenceScale', align: 'right', width: 150 },
        { title: '当日 MO 票领料数量', dataIndex: 'requisitionQuantity', align: 'right', width: 180 },
        { title: '当日 MO 票补料数量', dataIndex: 'replenishQuantity', align: 'right', width: 180 },
        { title: '当日 MO 票退料数量', dataIndex: 'backQuantity', align: 'right', width: 180 },
        { title: '当日超/省尺数', dataIndex: 'overQuantity', align: 'right', width: 150 },
        { title: '净用量', dataIndex: 'netQuantity', align: 'right', width: 130 },
        { title: '实际用量', dataIndex: 'actualQuantity', align: 'right', width: 130 },
        { title: '利用率', dataIndex: 'utilizationRate', align: 'right', width: 120 },
        { title: '超省率', dataIndex: 'overRate', align: 'right', width: 120 },
        { title: '计件工价', dataIndex: 'calWages', align: 'right', width: 130 },
        { title: '计件工资', dataIndex: 'calSalary', align: 'right', width: 130 },
        { title: '真皮超/省单价', dataIndex: 'laborWages', align: 'right', width: 150 },
        { title: '激励金额', dataIndex: 'incentiveAmount', align: 'right', width: 130 },
        { title: '批次号', dataIndex: 'lotNumbers', align: 'left', width: 240, ellipsis: true },
        { title: '是否让步皮', dataIndex: 'concessionLeather', align: 'center', width: 140, ellipsis: true },
        { title: '供应商编码', dataIndex: 'supplierCodes', align: 'left', width: 200, ellipsis: true },
        { title: '供应商名称', dataIndex: 'supplierNames', align: 'left', width: 240, ellipsis: true }
      ]
    }
  },
  mounted () {
    this.onSearch()
  },
  methods: {
    isValidProduceRange (dates) {
      if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) return false
      const start = moment(dates[0]).startOf('day')
      const end = moment(dates[1]).startOf('day')
      if (!start.isValid() || !end.isValid()) return false
      const maxEnd = start.clone().add(1, 'month').subtract(1, 'day')
      return !end.isBefore(start) && !end.isAfter(maxEnd)
    },
    disabledProduceDate (current) {
      if (!current || !this.produceDateAnchor) return false
      const start = this.produceDateAnchor.clone().startOf('day')
      const maxEnd = start.clone().add(1, 'month').subtract(1, 'day').endOf('day')
      return current.isBefore(start) || current.isAfter(maxEnd)
    },
    handleProduceCalendarChange (dates) {
      this.produceDateAnchor = dates && dates[0] ? moment(dates[0]).startOf('day') : undefined
    },
    handleProduceDateOpenChange (open) {
      if (!open) this.produceDateAnchor = undefined
    },
    handleProduceDateChange (dates) {
      if (!this.isValidProduceRange(dates)) {
        this.$message.warning('生产日期查询跨度不能超过一个月')
        this.$nextTick(() => {
          this.produceDates = [
            moment(this.queryParams.produceStartDate, 'YYYY-MM-DD HH:mm:ss'),
            moment(this.queryParams.produceEndDate, 'YYYY-MM-DD HH:mm:ss')
          ]
        })
        return
      }
      this.queryParams.produceStartDate = moment(dates[0]).format('YYYY-MM-DD 00:00:00')
      this.queryParams.produceEndDate = moment(dates[1]).format('YYYY-MM-DD 23:59:59')
    },
    onSearch (value = {}) {
      if (!this.isValidProduceRange(this.produceDates)) {
        this.$message.warning('生产日期查询跨度不能超过一个月')
        return
      }
      this.queryParams.produceStartDate = moment(this.produceDates[0]).format('YYYY-MM-DD 00:00:00')
      this.queryParams.produceEndDate = moment(this.produceDates[1]).format('YYYY-MM-DD 23:59:59')
      const params = {
        ...this.queryParams,
        ...value,
        produceStartDate: this.queryParams.produceStartDate,
        produceEndDate: this.queryParams.produceEndDate
      }
      delete params.searchFormLabel
      delete params.produceDate
      this.$refs.dataTable.searchParams = params
      this.$refs.dataTable.getData()
    },
    resetQuery () {
      this.queryParams = createDefaultQueryParams()
      this.produceDates = createDefaultProduceDates()
      this.produceDateAnchor = undefined
    },
    handleExport () {
      if (!this.isValidProduceRange(this.produceDates)) {
        this.$message.warning('生产日期查询跨度不能超过一个月')
        return
      }
      const params = { ...this.$refs.dataTable.searchParams }
      delete params.pageNum
      delete params.pageSize
      delete params.page
      this.$refs.download.openModal(params)
    }
  }
}
</script>

<style scoped lang="less">
.ant-calendar-picker {
  width: 100%;
}
</style>
